import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button, IconButton } from '@mes-amis/galette-design-system';
import { useLiveQuery } from 'dexie-react-hooks';
import { isEqual } from 'lodash';
import { isUserEdit, type FormChangeMeta } from '@/lib/form-change-dirty';
import { buildIncomingPatch } from '@/lib/form-incoming-patch';
import { updateDocument, finalizeDocument, pullDocument, advanceHLCFromRemoteTimestamps, confirmPrefill, confirmPrefills, handleRemoteLock, createDraftFromLockedOnlineConflict, getVirtualFields, stripUnauthorableLocal, stampRevealedFields, dismissWarning, undismissWarning } from '@/db/services/document_service';
import { finishTarget, isDocumentLocked, isUnderReview } from '@/db/types/document';
import { dateCompletedValue } from '@/lib/recorded_at';
import LockedOnlineConflictModal from '@/components/modals/LockedOnlineConflictModal';
import { lockedOnlineCopy } from '@/lib/locked_online';
import { createSignatures, fetchSignatures, getLocalSignatures, signSignatureSlot, storeServerSignatures, type SignSlotOptions } from '@/db/services/signature_service';
import type { Signature } from '@/db/types/signature';
import { getContactsForClient } from '@/db/services/contact_service';
import { officialFullName } from '@/db/types/person';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { pullTemplate } from '@/db/services/document_template_service';
import { isTemplateOfflineEnabled } from '@/db/types/document_template';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { useClientFromParam } from '../hooks/useClientFromParam';
import { useDocumentWebSocket } from '../hooks/useDocumentWebSocket';
import { useDocumentEditorReplayPolicy } from '../hooks/useDocumentEditorReplayPolicy';
import { decorateClient } from '@/db/types/client';
import db from '@/db/db';
import type { Document, ServerFieldErrors } from '@/db/types/document';
import { serverFieldErrorsToBlockers, serverFieldErrorsToValidationMap, externalErrorBlocker } from '@/lib/finalize_errors';
import { hasNewerWarningDismissals, mergeWarningDismissals, serverWarningDismissals, toEditorWarningDismissals, warningDismissalStamps } from '@/lib/warning_dismissals';
import { trackEvent } from '@/db/services/analytics_service';
import { getItem, setItem } from '@/db/services/application_session_service';
import { SIGNATURE_ID_PREFIX, compareSignatureSlots } from '@/db/types/signature';
import ContactModelDataProvider from '../components/contacts/ContactModelDataProvider';
import { createLogger, LogLevel } from '../lib/logger';
import { mergeDocumentCRDT, type MergeResult } from '../lib/crdt/document-crdt';
import { getClientUUID } from '../lib/crdt/client-uuid';
import {
  type Document as SuperFormDocument,
  type SuperFormDefinition,
  type FormDataStoreActions,
  type FieldNoteEntry,
  DocumentTabs,
  type DocumentTab,
  SuperForm,
  DocumentNavigationSidebar,
  SummaryTab,
  SignatureTab,
  type DocumentSignature,
  type SignOptions,
  type RepresentativeSigning,
  DocumentPanelWrapper,
  ErrorNavigation,
  type NavigableError,
  type SuperFormPrefillMap,
  FormDataStoreProvider,
  FieldPermissionsProvider,
  HelpDocumentProvider,
  useHelpDocument,
  ErrorReporterProvider,
  type SuperFormError,
  NotesProvider,
  PrefillProvider,
  PrefillBanner,
  buildPrefillFormResolvers,
  walkFormConfig,
  useFormData,
  extractNotesFromFormData,
  validateFormData,
  validationErrorsToObject,
  DateCompletedInput,
  superFormKeyForNote,
  isPresent,
  TitleBar,
  ExternalErrorsBanner,
  type ExternalError,
  DocumentMeta,
  useMediaQuery,
  PHONE_MEDIA_QUERY,
  TimezoneProvider,
  IssuesProvider,
  IssueControls,
  type IssueBlocker,
  type IssuesActions,
  type SoftValidationWarning,
} from '@mes-amis/super-form';
import { resolveOnlineErrorHref } from '@/lib/external_errors';
import { usesFloatingIssueControls } from '@/lib/issue_controls';

const logger = createLogger('DocumentEdit', LogLevel.DEBUG);

// Forward super-form's internal, non-throwing error reports to Sentry. These
// are unexpected-but-recoverable conditions super-form surfaces instead of
// crashing the render tree (e.g. a form definition referencing a specialized
// field type it has no renderer for). Module-level so the reference is stable
// across renders (ErrorReporterProvider memoizes on the callback identity).
const reportSuperFormError = (report: SuperFormError): void => {
  // Truthiness, not `!== undefined`: SuperFormError.error is typed `unknown`,
  // so it can be null/falsy. captureException(null) yields a low-value
  // "Non-Error exception captured" issue, so only take that path for a real
  // error value and fall back to captureMessage otherwise.
  if (report.error) {
    Sentry.captureException(report.error, {
      extra: { ...report.context, message: report.message },
    });
  } else {
    Sentry.captureMessage(report.message, {
      level: 'error',
      extra: report.context,
    });
  }
};

/**
 * Walk a section config and collect all leaf-field slugs. Used by the
 * Finish-time error-nav synthesis to count outstanding PARC entries per
 * section. Uses the package's `walkFormConfig` so the slug shape matches
 * everywhere else (column key for FieldTable cells, store key for
 * BasicList). A previous implementation emitted `${rowId}__${col}` for
 * FieldTable cells and never matched `field_prefills` keys — sections
 * whose only PARC entries were table cells silently bypassed the gate.
 */
function collectSectionSlugs(config: Record<string, any>): string[] {
  const out: string[] = [];
  walkFormConfig({ __section__: config as any }, ({ name, config: visited }) => {
    if (!visited) return;
    const slug = visited.type === 'BasicList' && typeof (visited as { store?: unknown }).store === 'string'
      ? (visited as { store: string }).store
      : name;
    out.push(slug);
  });
  return out;
}

/** Small button that toggles the Help / Notes panel. Must be rendered inside HelpDocumentProvider.
 *  Returns null when there is nothing to show (no help content and no notes enabled). */
function PanelToggleButton({ hasPanel }: { hasPanel: boolean }) {
  const { isOpen, openHelp, closeHelp } = useHelpDocument();

  if (!hasPanel) return null;

  return (
    <IconButton
      variant='tertiary'
      onClick={() => isOpen ? closeHelp() : openHelp()}
      aria-label="Toggle help and notes panel"
      title={isOpen ? 'Close help and notes panel' : 'Open help and notes panel'}
      icon='side-panel'
    />
  );
}

/** Pure helper. Returns the set of PARC slugs that are still outstanding:
 *  required confirmation + not yet confirmed + visible + not readonly. The
 *  visibility check (`isVisible`) is evaluated against the supplied
 *  formData snapshot — callers must pass a fresh snapshot (either the
 *  store's `useFormData()` result inside the store, or
 *  `currentFormDataRef.current` from outside). Pure / side-effect free so
 *  it's safe to call from useMemo, useEffect, or click handlers. */
function computeUnconfirmedPrefills(
  prefills: SuperFormPrefillMap | undefined,
  confirmedPrefillsSet: ReadonlySet<string>,
  prefillResolvers: { isReadOnly: (slug: string) => boolean; isVisible: (slug: string, formData: Record<string, unknown>) => boolean },
  formDataSnapshot: Record<string, unknown>,
): Set<string> {
  const out = new Set<string>();
  if (!prefills) return out;
  for (const [slug, entry] of Object.entries(prefills)) {
    if (!entry?.requires_confirmation) continue;
    if (confirmedPrefillsSet.has(slug)) continue;
    if (prefillResolvers.isReadOnly(slug)) continue;
    if (!prefillResolvers.isVisible(slug, formDataSnapshot)) continue;
    out.add(slug);
  }
  return out;
}

/** Pure helper. Given a form definition and a pending-PARC set, returns the
 *  ordered list of pf-section error entries (one per top-level FieldGroup
 *  with at least one outstanding PARC slug). */
function buildPfSectionErrors(
  formDef: Record<string, unknown>,
  pendingSet: ReadonlySet<string>,
): Array<{ field: string; label: string; message: string; kind: 'pf-section' }> {
  const out: Array<{ field: string; label: string; message: string; kind: 'pf-section' }> = [];
  for (const [sectionId, sectionCfg] of Object.entries(formDef)) {
    if (!sectionCfg || (sectionCfg as { type?: string }).type !== 'FieldGroup') continue;
    const slugs = collectSectionSlugs(sectionCfg as Record<string, unknown>);
    const pending = slugs.filter((slug) => pendingSet.has(slug));
    if (pending.length === 0) continue;
    const cfg = sectionCfg as { title?: string; label?: string };
    const title = cfg.title || cfg.label || sectionId;
    out.push({
      field: sectionId,
      kind: 'pf-section',
      label: title,
      message: `${pending.length} pre-filled ${pending.length === 1 ? 'question needs' : 'questions need'} confirmation`,
    });
  }
  return out;
}

/** PrefillProvider variant that derives `visiblePrefills` from the
 *  surrounding FormDataStoreProvider. Same rationale as
 *  `NotesProviderFromStore`: the visibility filter depends on formData
 *  which post-perf-refactor no longer lives in DocumentEdit's state, so
 *  the derivation must happen inside the store. */
function PrefillProviderFromStore({
  prefills,
  prefillResolvers,
  confirmedPrefillsSet,
  confirmAllEnabled,
  clientName,
  onPrefillConfirmed,
  onPrefillsConfirmed,
  children,
}: {
  prefills: SuperFormPrefillMap | undefined;
  prefillResolvers: ReturnType<typeof buildPrefillFormResolvers>;
  confirmedPrefillsSet: ReadonlySet<string>;
  confirmAllEnabled: boolean;
  clientName: string | undefined;
  onPrefillConfirmed: (slug: string) => void;
  onPrefillsConfirmed: (slugs: string[]) => void;
  children: ReactNode;
}) {
  const formData = useFormData();
  const visiblePrefills = useMemo(() => {
    if (!prefills) return undefined;
    const out: typeof prefills = {};
    for (const [slug, entry] of Object.entries(prefills)) {
      if (prefillResolvers.isReadOnly(slug)) continue;
      if (!prefillResolvers.isVisible(slug, formData)) continue;
      out[slug] = entry;
    }
    return out;
  }, [prefills, prefillResolvers, formData]);
  return (
    <PrefillProvider
      prefills={visiblePrefills}
      confirmedPrefillsSet={confirmedPrefillsSet}
      confirmAllEnabled={confirmAllEnabled}
      clientName={clientName}
      resolveLabel={prefillResolvers.resolveLabel}
      resolveSection={prefillResolvers.resolveSection}
      resolveSubsection={prefillResolvers.resolveSubsection}
      resolveDisplayValue={prefillResolvers.resolveDisplayValue}
      resolveRowGroup={prefillResolvers.resolveRowGroup}
      resolveOrder={prefillResolvers.resolveOrder}
      onPrefillConfirmed={onPrefillConfirmed}
      onPrefillsConfirmed={onPrefillsConfirmed}
    >
      {children}
    </PrefillProvider>
  );
}

/** Keeps the error banner's pf-section entries live as the user confirms
 *  slugs (via section pill, blue dot, focus, etc.) OR as formData changes
 *  toggle section visibility. Mounted inside FormDataStoreProvider so
 *  `useFormData()` gives us a reactive view of the data the user is
 *  editing. Side effect only — renders nothing. */
function ParcErrorNavSync({
  prefills,
  prefillResolvers,
  confirmedPrefillsSet,
  formDef,
  setPfSectionErrors,
}: {
  prefills: SuperFormPrefillMap | undefined;
  prefillResolvers: ReturnType<typeof buildPrefillFormResolvers>;
  confirmedPrefillsSet: ReadonlySet<string>;
  formDef: Record<string, unknown> | undefined;
  setPfSectionErrors: React.Dispatch<React.SetStateAction<NavigableError[]>>;
}) {
  const formData = useFormData();
  useEffect(() => {
    if (!formDef) return;
    setPfSectionErrors((prev) => {
      if (prev.length === 0) return prev; // banner not showing them yet
      const pendingSet = computeUnconfirmedPrefills(prefills, confirmedPrefillsSet, prefillResolvers, formData);
      return buildPfSectionErrors(formDef, pendingSet);
    });
  }, [formData, prefills, confirmedPrefillsSet, prefillResolvers, formDef, setPfSectionErrors]);
  return null;
}

/** NotesProvider variant that derives `notes` from the surrounding
 *  FormDataStoreProvider. Mounted inside the form-data provider so it can
 *  subscribe to formData without forcing DocumentEdit to re-render.
 *
 *  Perf note: useFormData() necessarily fires this component's render on
 *  every keystroke (super-form 0.2.x doesn't expose a key-prefix
 *  subscription). To prevent that cascade from re-rendering ~700
 *  FieldLabel / InlineRadioButtonsField / etc. components that consume
 *  NotesContext, we keep the `notes` array reference stable when the
 *  notes content hasn't changed. Combined with upstream memoization of
 *  NotesProvider's context value (filed upstream), context consumers
 *  bail on non-note field changes. Until that upstream change lands the
 *  consumers still re-render — but the rest of this work (sort, label
 *  derivation, array allocation) is at least avoided. */
function NotesProviderFromStore({
  definition,
  onOpenNotesToField,
  children,
}: {
  definition: SuperFormDefinition | undefined;
  onOpenNotesToField: (fieldSlug: string, fieldLabel?: string) => void;
  children: ReactNode;
}) {
  const formData = useFormData();
  const prevNotesRef = useRef<FieldNoteEntry[] | null>(null);
  const notes = useMemo(() => {
    const next = extractNotesFromFormData(formData, definition);
    const prev = prevNotesRef.current;
    if (prev && notesEqual(prev, next)) {
      return prev;
    }
    prevNotesRef.current = next;
    return next;
  }, [formData, definition]);
  return (
    <NotesProvider notes={notes} onOpenNotesToField={onOpenNotesToField}>
      {children}
    </NotesProvider>
  );
}

/** Shallow comparison of two FieldNoteEntry arrays. Returns true when the
 *  visible content is identical, so the caller can reuse the prior array
 *  reference and keep context consumers from churning. */
function notesEqual(a: FieldNoteEntry[], b: FieldNoteEntry[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i];
    const y = b[i];
    if (x.key !== y.key || x.detail !== y.detail || x.scope !== y.scope || x.label !== y.label) {
      return false;
    }
  }
  return true;
}

export default function DocumentEdit() {
  const { assessmentId, documentId, localId } = useParams<{ id: string; assessmentId?: string; documentId?: string; localId?: string }>();
  const navigate = useNavigate();
  const { client, clientId, clientSlug, loading: clientLoading } = useClientFromParam();
  // Support both assessmentId (for backward compatibility) and documentId params
  // documentId/assessmentId is for server documents (number), localId is for drafts (string like "draft-xxx")
  const documentIdNum = documentId ? parseInt(documentId, 10) : (assessmentId ? parseInt(assessmentId, 10) : null);
  const isDraftRoute = Boolean(localId);

  // Get current person for organization scoping and signature capabilities
  const { currentPerson, organizationId } = useCurrentPerson();

  // Decorate client with computed properties (fullName, initials)
  const decoratedClient = useMemo(() => {
    return client ? decorateClient(client) : client;
  }, [client]);

  // Use useLiveQuery to get document from the database
  // For draft routes, query by local_id; for server routes, query by id
  // Returns: undefined = still loading, null = not found, Document = found
  const document = useLiveQuery(async () => {
    if (isDraftRoute) {
      // Draft route - query by local_id
      if (!localId) return undefined;
      return (await db.documents.where('local_id').equals(localId).first()) ?? null;
    } else {
      // Server route - query by id
      if (!documentIdNum || isNaN(documentIdNum)) return undefined;
      return (await db.documents.get(documentIdNum)) ?? null;
    }
  }, [isDraftRoute, localId, documentIdNum]);

  // Redirect from draft URL to server URL after sync completes
  useEffect(() => {
    if (isDraftRoute && document && document.synced && document.id) {
      if (!clientSlug) {
        console.warn(`Draft ${localId} synced with server ID ${document.id} but clientSlug is missing — cannot redirect`);
        return;
      }
      // Draft has been synced and now has a server ID - redirect to server URL
      console.log(`Draft ${localId} synced with server ID ${document.id}, redirecting...`);
      navigate(`/clients/${clientSlug}/documents/${document.id}/edit`, { replace: true });
    }
  }, [isDraftRoute, document, localId, clientSlug, navigate]);

  // Use a stable reference to document.template_id to prevent template query from re-running
  const templateId = useMemo(() => document?.template_id, [document?.template_id]);

  // Use useLiveQuery to get template from the database
  const template = useLiveQuery(async () => {
    // If no template needed, return null (not undefined) to signal "done loading"
    if (!templateId) return null;

    // Wait for organizationId to be ready
    if (!organizationId) return undefined;

    const templateData = await db.documentTemplates.get(templateId);

    // Verify template belongs to current organization
    if (templateData && templateData.organization_id === organizationId) {
      return templateData;
    }

    return null; // Return null if template doesn't exist or wrong org
  }, [templateId, organizationId]);

  // Extract help document slugs from the template.
  // Form-level help (definition.help.slug) applies to the edit tab; summary-level
  // help (summary.help.slug) applies to the summary tab and is what CAP Manual
  // buttons point into. Either may be absent.
  const formHelpSlug = useMemo(() => {
    return template?.super_form?.definition?.help?.slug || null;
  }, [template]);

  const summaryHelpSlug = useMemo(() => {
    return template?.super_form?.definition?.summary?.help?.slug || null;
  }, [template]);

  // Load each help document independently so the panel can decide what to
  // render without waiting on a tab switch (and so we don't show an empty
  // panel when a slug is configured but the doc isn't cached offline).
  const formHelpDocument = useLiveQuery(async () => {
    if (!formHelpSlug) return null;
    return await db.helpDocuments.where('slug').equals(formHelpSlug).first() || null;
  }, [formHelpSlug]);

  const summaryHelpDocument = useLiveQuery(async () => {
    if (!summaryHelpSlug) return null;
    return await db.helpDocuments.where('slug').equals(summaryHelpSlug).first() || null;
  }, [summaryHelpSlug]);

  // Does the template allow notes in principle? Summary-tab notes are
  // suppressed separately via `notesVisible`.
  const notesEnabled = !!template && (template.field_notes_enabled !== false || template.document_note_enabled !== false);

  // Check if document is completed
  const isComplete = document?.status === 'completed';
  const isWaitingForSignatures = ['waiting_for_signature', 'signing_in_progress'].includes(document?.status || '');
  const isDeletedOnline = document?.sync_error_type === 'deleted_online';
  // Completed or sent for review elsewhere while open here. The server copy was adopted and the
  // user's differing edits are preserved for the conflict modal.
  const lockedOnline = document?.sync_error_type === 'completed_online' ? lockedOnlineCopy(document) : null;
  const isLockedOnline = !!lockedOnline;
  const isInReview = document?.status === 'in_review';
  const finishesInReview = !!document && finishTarget(document, template, currentPerson) === 'in_review';
  const isChangesRequested = document?.status === 'changes_requested';
  // The status checks cover a local finalize the stale server `locked` flag doesn't know about yet
  const isFormReadonly = isComplete || isWaitingForSignatures || isInReview || isChangesRequested || (!!document && isDocumentLocked(document)) || isDeletedOnline || isLockedOnline;
  // Ref mirror so the unmount cleanup (which captures nothing) can read the latest value
  const isCompleteRef = useRef<boolean>(false);
  isCompleteRef.current = isFormReadonly;

  // Check if template supports offline use
  const templateOfflineEnabled = isTemplateOfflineEnabled(template);

  // Load signatures for completed documents and documents in the signing window
  const hasSignatureActivity = isComplete || isWaitingForSignatures;
  const signatures = useLiveQuery(async () => {
    if (!document?.id || !hasSignatureActivity) return [];
    return await db.signatures.where('document_id').equals(document.id).toArray();
  }, [document?.id, hasSignatureActivity]);

  // Candidate signers for the Client/Legal Representative slot — powers the
  // signer identity question in super-form's SignatureCard. This is the local
  // FALLBACK for rows created offline; rows fetched from the server carry an
  // authoritative representative_signing (with the Caregiver/Client label the
  // offline app cannot derive) which takes precedence in signatureTabSlots.
  const legalRepContact = useLiveQuery(async () => {
    if (!clientId || !hasSignatureActivity) return undefined;
    const contacts = await getContactsForClient(clientId);
    return contacts.find((contact) => contact.legal_representative && contact.person?.id) ?? null;
  }, [clientId, hasSignatureActivity]);

  const representativeSigning = useMemo<RepresentativeSigning | undefined>(() => {
    if (!client?.person?.id) return undefined;
    const legalRepPerson = legalRepContact?.person;
    return {
      client: { person_id: client.person.id, official_full_name: officialFullName(client.person) },
      legal_representative: legalRepPerson?.id
        ? { person_id: legalRepPerson.id, official_full_name: officialFullName(legalRepPerson) }
        : null,
    };
  }, [client?.person, legalRepContact]);

  // Refresh the cached signatures from the server when online. Signatures can
  // change server-side (e.g. a re-open + re-sign online); storeServerSignatures
  // never clobbers signatures captured locally that still await sync.
  useEffect(() => {
    const fetchSignaturesFromServer = async () => {
      if (!hasSignatureActivity || !document?.id || !clientId || !navigator.onLine) return;

      try {
        const serverSignatures = await fetchSignatures(clientId, document.id as number);
        await storeServerSignatures(document.id as number, serverSignatures);
      } catch (err) {
        logger.error('Failed to fetch signatures', { documentId: document.id, error: err });
      }
    };

    fetchSignaturesFromServer();
  }, [hasSignatureActivity, document?.id, clientId]);

  // FormDataStoreProvider owns the formData ref. We hold the initial snapshot
  // here just long enough to seed the provider on mount; all subsequent reads
  // go through currentFormDataRef (already kept in sync by handleFormDataChange).
  // Removing formData from React state stops DocumentEdit from re-rendering on
  // every keystroke — the heavy chrome (header, status, tab buttons) and the
  // memoized Sidebar/Panel/Summary subtrees no longer reconcile.
  const initialFormDataRef = useRef<Record<string, unknown>>({});
  const formDataActionsRef = useRef<FormDataStoreActions | null>(null);
  const [formDataInitialized, setFormDataInitialized] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  // In-editor resolution for a completed_online conflict (diff view + create-new/discard)
  const [showLockedOnlineModal, setShowLockedOnlineModal] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  // Deep link from the documents list signature counts (?tab=signature)
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<DocumentTab>(searchParams.get('tab') === 'signature' ? 'signature' : 'document');

  // A stale `?tab=signature` deep link can land on a document that has no signature
  // config; the signature tab isn't valid there, so fall back to the form once the
  // template has loaded. (Guard on `template` so we don't reset before it resolves.)
  useEffect(() => {
    if (activeTab === 'signature' && template && !template.signature_config) {
      setActiveTab('document');
    }
  }, [activeTab, template]);

  // Keep the URL's `?tab` in sync with the active tab so the signature deep link is
  // shareable/reloadable rather than only being read once on mount.
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    if (activeTab === 'signature') next.set('tab', 'signature');
    else next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [activeTab, setSearchParams]);

  // At phone width the document meta (title, status, client name, id) moves out
  // of the fixed title bar and into the scrollable tab content — matching
  // super-form's DocumentEditor. Reuses the package's DocumentMeta + breakpoint.
  // Pause Sentry Session Replay for the editor visit in builds that opt out
  // (VITE_DISABLE_REPLAY_IN_DOCUMENT_EDITOR); the observer's per-interaction
  // cost on large forms is what it guards.
  useDocumentEditorReplayPolicy();

  const isPhone = useMediaQuery(PHONE_MEDIA_QUERY);
  const metaProps = useMemo(() => ({
    title: template?.name || 'Document',
    status: document?.status,
    // Signature-flow documents read "Signed" once completed
    signed: !!template?.signature_config,
    clientName: decoratedClient?.person.displayName || 'Unknown Client',
    clientUrl: clientSlug ? `/clients/${clientSlug}` : undefined,
    clientSlug: client?.label,
  }), [template?.name, template?.signature_config, document?.status, decoratedClient?.person.displayName, clientSlug, client?.label]);

  // Pick which help document to expose to the panel based on the active tab.
  // No cross-tab fallback: Manual button anchors on the summary tab point into
  // the CAP manual (summary.help.slug) and would miss against the form-level
  // document, so we'd rather show nothing than the wrong doc.
  const helpDocument = useMemo(() => {
    return activeTab === 'summary' ? summaryHelpDocument || null : formHelpDocument || null;
  }, [activeTab, formHelpDocument, summaryHelpDocument]);

  // The panel is visible when the current tab has something to render:
  // summary tab → the CAP manual; document tab → the form help doc or notes.
  const hasPanel = activeTab === 'summary'
    ? !!summaryHelpDocument
    : !!formHelpDocument || notesEnabled;
  // Lazy-mount SummaryTab on first visit to avoid expensive initialization on page load.
  // (DocumentEditor has its own independent flag for apps using that component directly.)
  const [summaryVisited, setSummaryVisited] = useState(false);
  const [autoSaving, setAutoSaving] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isFinishing, setIsFinishing] = useState<boolean>(false);
  // Blockers the floating issue controls list after a Finish attempt: client
  // validation, on-form errors from a rejected finalize, and anything the
  // finalize couldn't attribute to a field.
  const [finishBlockers, setFinishBlockers] = useState<IssueBlocker[]>([]);
  // Errors on fields not rendered on this form (e.g. the client record). Surfaced
  // after a finalize attempt, mirroring the online editor — not on initial load.
  const [externalErrors, setExternalErrors] = useState<ExternalError[]>([]);
  // Open soft-validation warnings a rejected finalize reported.
  const [serverWarnings, setServerWarnings] = useState<SoftValidationWarning[]>([]);
  const issuesActionsRef = useRef<IssuesActions | null>(null);
  // Without the floating issue controls, the error banner's one entry per section
  // with unconfirmed pre-fills: set by a Finish attempt, kept live by ParcErrorNavSync.
  const [pfSectionErrors, setPfSectionErrors] = useState<NavigableError[]>([]);
  // Set when the persisted outcome of a failed sync-replay finalize is hydrated:
  // the issue controls then open on it as if Finish had just been pressed.
  const [hydratedFinishAttempt, setHydratedFinishAttempt] = useState(false);
  const [focusedNoteField, setFocusedNoteField] = useState<string | null>(null);
  const [focusedNoteFieldLabel, setFocusedNoteFieldLabel] = useState<string | undefined>(undefined);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [sidebarWidth, setSidebarWidth] = useState<number | undefined>(undefined);
  const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined);

  // Hydrate the fix-elsewhere banner from a persisted finalize attempt. The
  // offline finish path can't reach the server, so external_errors only arrive
  // when the background sync layer replays finalize — possibly while this page
  // is open, or before the user reopens the document. Mirror the persisted
  // value into banner state once per distinct payload (a session edit clears the
  // banner in-memory; we don't want autosave re-popping it), without clobbering
  // a fresh in-session online finish attempt (that path doesn't persist).
  const hydratedExternalErrorsRef = useRef<string>('');
  useEffect(() => {
    const persisted = document?.external_errors;
    if (!persisted || persisted.length === 0) return;
    const signature = JSON.stringify(persisted);
    if (hydratedExternalErrorsRef.current === signature) return;
    hydratedExternalErrorsRef.current = signature;
    setExternalErrors(persisted);
    setHydratedFinishAttempt(true);
  }, [document?.external_errors]);

  // Map server field errors (slug → {message, label}) into the editor's two
  // error stores: the Blockers the issue controls list and the inline field
  // marks. The server is authoritative about which fields a finalize rejected,
  // so its entries replace earlier field Blockers; anything without a field
  // (a system error) stays. The interactive finish path reaches here only once
  // client validation has passed.
  const applyServerFieldErrors = useCallback((fieldErrors: ServerFieldErrors) => {
    setFinishBlockers((prev) => [...serverFieldErrorsToBlockers(fieldErrors), ...prev.filter((b) => b.kind !== 'field')]);
    setValidationErrors((prev) => ({ ...prev, ...serverFieldErrorsToValidationMap(fieldErrors) }));
  }, []);

  // Hydrate open soft-validation warnings from a failed sync-replay finalize,
  // same lifecycle as the fix-elsewhere errors above.
  const hydratedWarningsRef = useRef<string>('');
  useEffect(() => {
    const persisted = document?.finalize_warnings;
    if (!persisted || persisted.length === 0) return;
    const signature = JSON.stringify(persisted);
    if (hydratedWarningsRef.current === signature) return;
    hydratedWarningsRef.current = signature;
    setServerWarnings(persisted);
    setHydratedFinishAttempt(true);
  }, [document?.finalize_warnings]);

  // Hydrate on-form field errors from a failed sync-replay finalize, same
  // lifecycle as the fix-elsewhere banner above: once per distinct payload,
  // without re-popping errors a session edit already retired.
  const hydratedFieldErrorsRef = useRef<string>('');
  useEffect(() => {
    const persisted = document?.finalize_field_errors;
    if (!persisted || Object.keys(persisted).length === 0) return;
    const signature = JSON.stringify(persisted);
    if (hydratedFieldErrorsRef.current === signature) return;
    hydratedFieldErrorsRef.current = signature;
    setHydratedFinishAttempt(true);
    applyServerFieldErrors(persisted);
  }, [document?.finalize_field_errors, applyServerFieldErrors]);

  // A hydrated failed finalize opens the issue controls on its outcome, as if
  // Finish had just been pressed. The provider mounts with the form (once the
  // client, template and form data have all loaded), so this checks after every
  // render until it has registered its actions; the ref keeps it to once.
  const hydratedSubmitDoneRef = useRef(false);
  useEffect(() => {
    if (!hydratedFinishAttempt || hydratedSubmitDoneRef.current || !issuesActionsRef.current) return;
    hydratedSubmitDoneRef.current = true;
    issuesActionsRef.current.submit();
  });

  // Load sidebar/panel state from applicationSession on mount
  useEffect(() => {
    getItem<boolean>('sidebar_collapsed').then((value) => {
      if (value !== null) {
        setSidebarCollapsed(value);
      }
    });
    getItem<number>('sidebar_width').then((value) => {
      if (value !== null) {
        setSidebarWidth(value);
      }
    });
    getItem<number>('panel_width').then((value) => {
      if (value !== null) {
        setPanelWidth(value);
      }
    });
  }, []);

  const handleSidebarCollapsedChange = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    setItem('sidebar_collapsed', collapsed);
  }, []);

  const handleSidebarWidthChange = useCallback((width: number) => {
    setSidebarWidth(width);
    setItem('sidebar_width', width);
  }, []);

  const handlePanelWidthChange = useCallback((width: number) => {
    setPanelWidth(width);
    setItem('panel_width', width);
  }, []);

  // Subscribe to real-time document updates via WebSocket
  const { connected: wsConnected } = useDocumentWebSocket({
    clientId: clientId || null,
    documentId: typeof document?.id === 'number' ? document.id : null,
    personId: currentPerson?.id || null,
    enabled: Boolean(document?.id) && !isDraftRoute, // Only for synced documents
    onUpdate: async (update, streamKey) => {
      // Guard against undefined update.content (silently ignore - this is a normal occurrence)
      if (!update.content) {
        return;
      }

      // Extract document data from the WebSocket message
      // The message format is JSON: { type: "document_update", document: { id, revision, super_form, ... } }
      let updatedDocument: Document | null = null;
      let newRevision: string | null = null;
      try {
        // The update.content could be the parsed JSON object or a string
        const messageData = typeof update.content === 'string'
          ? JSON.parse(update.content)
          : update.content;

        // Extract document object - it should already match the Document API format
        if (messageData && typeof messageData === 'object' && 'document' in messageData) {
          updatedDocument = messageData.document;
          newRevision = updatedDocument?.revision || null;
        }
      } catch (error) {
        logger.error('Failed to parse WebSocket update', { error });
        return;
      }

      // If no document data, ignore the update
      if (!updatedDocument || !newRevision) {
        logger.debug('WebSocket update has no document data, ignoring');
        return;
      }

      // Locked-elsewhere detection runs BEFORE the revision-based echo/dedup
      // suppression: a finalize changes only the status, not the field HLCs,
      // so its broadcast can carry the same revision as our own last save and
      // would otherwise be discarded as an echo.
      if (isDocumentLocked(updatedDocument) && document?.id && clientId) {
        const wasLocked = isDocumentLocked(document);
        await handleRemoteLock(clientId, document.id, updatedDocument);
        // The conflict case explains itself with the completed_online banner and
        // review with its own banner; the clean case flips read-only silently, so say why.
        const after = await db.documents.get(document.id);
        if (!wasLocked && after && after.sync_error_type !== 'completed_online' &&
            !isUnderReview(after) && isDocumentLocked(after)) {
          setWarning('This document was completed elsewhere — it is now read-only.');
        }
      }

      // A save that only records a warning dismissal leaves the answers (and
      // possibly the revision) unchanged, so the revision-based skips below
      // must not drop a broadcast bringing dismissals this device hasn't seen.
      const incomingDismissals = serverWarningDismissals(updatedDocument.warning_dismissals);
      const bringsDismissals = hasNewerWarningDismissals(document?.warning_dismissals, incomingDismissals);

      // Skip our own save echoing back on any stream (prevents save→broadcast→save loops).
      if (newRevision === lastRevisionRef.current && !bringsDismissals) {
        logger.debug('Ignoring WebSocket echo of our own save', {
          documentId: document?.id,
          revision: newRevision,
          lastRevision: lastRevisionRef.current
        });
        return;
      }

      // Skip a revision already applied on THIS stream. Tracking per-stream (rather
      // than one global revision) lets a single remote save that touches both a
      // sensitive and a non-sensitive field — broadcast on two streams with the
      // same revision — apply on each stream instead of the second being deduped.
      if (newRevision === appliedRevisionByStreamRef.current[streamKey] && !bringsDismissals) {
        logger.debug('Ignoring already-applied revision on stream', {
          documentId: document?.id,
          revision: newRevision,
          streamKey
        });
        return;
      }

      // Store old per-stream revision for rollback on failure
      const oldStreamRevision = appliedRevisionByStreamRef.current[streamKey] ?? null;

      logger.info('Document update received from WebSocket, applying directly', {
        documentId: document?.id,
        timestamp: update.timestamp,
        revision: newRevision,
        streamKey,
        oldStreamRevision,
        hasLocalChanges: document?.synced === false
      });

      // Apply the update directly from WebSocket data (no server pull needed!)
      // If we have local unsynced changes, the CRDT merge will handle conflicts
      if (document?.id && clientId) {
        // Resolve clientUUID outside the transaction to avoid TransactionInactiveError
        // (getClientUUID reads from db.applicationSession which is not in this transaction's scope)
        let clientUUID: string;
        try {
          clientUUID = await getClientUUID();
        } catch (error) {
          logger.error('Failed to get client UUID for WebSocket CRDT merge', { error });
          return;
        }

        // Resolve template virtual fields outside the transaction too — getVirtualFields
        // reads db.documentTemplates, which is not in this transaction's ('rw', db.documents) scope.
        const virtualFields = await getVirtualFields(document.template_id);

        // Local values the merge discards (server won) — captured inside the
        // transaction, emitted as an analytics event after it (trackEvent touches
        // tables outside this transaction's scope).
        let crdtDiscarded: MergeResult['discarded'] = [];

        // Use a transaction to prevent race condition between read and write
        // This ensures user's concurrent saves won't be overwritten
        await db.transaction('rw', db.documents, async () => {
          const existingDoc = await db.documents.get(document.id!);

          if (!existingDoc) {
            logger.warn('Document not found in local DB during WebSocket update');
            return;
          }

          // Start from existing local document to preserve local-only fields and status.
          // Only selectively apply server updates — never blindly overwrite.
          const serverSuperForm = updatedDocument!.super_form;
          const serverMeta = updatedDocument!.super_form_meta;

          // Check if document data actually changed (skip if WebSocket is just echoing our own save)
          const superFormChanged = !isEqual(existingDoc.super_form, serverSuperForm);
          // Warning dismissals merge per key, the newer changed_hlc winning.
          const mergedDismissals = mergeWarningDismissals(existingDoc.warning_dismissals, incomingDismissals);
          const dismissalsChanged = !isEqual(mergedDismissals, existingDoc.warning_dismissals ?? []);
          const dataChanged = superFormChanged || dismissalsChanged ||
            existingDoc.updated_at !== updatedDocument!.updated_at;

          if (!dataChanged && existingDoc.revision === newRevision) {
            logger.debug('WebSocket update matches existing data, skipping', {
              documentId: document.id,
              revision: newRevision
            });
            return;
          }

          // Build merged document starting from local state
          const mergedDoc = {
            ...existingDoc,
            // Update revision and timestamp from server
            revision: newRevision,
            updated_at: updatedDocument!.updated_at || existingDoc.updated_at,
            warning_dismissals: mergedDismissals,
            // Preserve local status — status changes come from explicit user actions, not broadcasts
          };
          advanceHLCFromRemoteTimestamps(clientUUID, warningDismissalStamps(incomingDismissals));
          const pendingDismissals = mergedDismissals.some((item) => item.pending);

          // Only merge super_form if server actually provided data and it differs
          if (superFormChanged && serverSuperForm && Object.keys(serverSuperForm).length > 0) {
            // ALWAYS CRDT-merge — never branch on the `synced` flag. The merge
            // unions all field keys, so a partitioned/partial broadcast (main =
            // non-sensitive fields, each permission stream = only its key's
            // fields) preserves the local-only fields it omits, while HLC stamps
            // decide every overlapping field. A local field lacking a stamp
            // correctly loses to the server; a newer local stamp correctly wins.
            // Exclude fields this device cannot author server state for — template
            // virtual/disabled fields and restricted sensitive slugs — from the
            // LOCAL side, matching pullDocument/refreshClientDocuments. Left in, the
            // editor's locally-materialized virtual values register as spurious local
            // wins on every collaborator broadcast, flipping a clean doc to unsynced
            // and scheduling a redundant push. (virtualFields prefetched above.)
            const local = stripUnauthorableLocal(existingDoc, virtualFields, existingDoc.field_permissions);

            const mergeResult = mergeDocumentCRDT(
              {
                super_form: local.superForm || {},
                super_form_meta: local.meta
              },
              {
                super_form: serverSuperForm,
                super_form_meta: serverMeta
              },
              clientUUID
            );

            mergedDoc.super_form = mergeResult.merged_form;
            mergedDoc.super_form_meta = mergeResult.merged_meta;
            // Stay unsynced whenever a local value (or dismissal) won — those
            // edits still need to be pushed to the server.
            mergedDoc.synced = mergeResult.local_wins === 0 && !pendingDismissals;

            // Advance local HLC to track causality from remote timestamps
            advanceHLCFromRemoteTimestamps(clientUUID, mergeResult.merged_meta);
            crdtDiscarded = mergeResult.discarded;

            if (mergeResult.fields_diverged > 0) {
              logger.debug('CRDT merge during WebSocket update', {
                fieldsDiverged: mergeResult.fields_diverged,
                localWins: mergeResult.local_wins,
                remoteWins: mergeResult.remote_wins
              });
            }
          } else {
            // Server didn't provide meaningful super_form data — keep local form
            // data AND local sync state (forcing synced:true here would strand
            // any un-pushed local edits behind the next pull's overwrite).
            logger.debug('WebSocket update has no super_form changes, keeping local data', {
              documentId: document.id,
              serverHasSuperForm: Boolean(serverSuperForm),
              serverKeyCount: serverSuperForm ? Object.keys(serverSuperForm).length : 0
            });
            mergedDoc.synced = existingDoc.synced;
          }

          await db.documents.put(mergedDoc);

          // Mark this revision applied for this stream (inside the transaction so
          // it only advances on success).
          appliedRevisionByStreamRef.current[streamKey] = newRevision;

          logger.info('Document updated from WebSocket', {
            documentId: document.id,
            revision: newRevision,
            streamKey
          });
        }).catch((error) => {
          // Rollback per-stream revision on failure to allow retry
          appliedRevisionByStreamRef.current[streamKey] = oldStreamRevision;
          logger.error('Failed to apply WebSocket update', {
            documentId: document.id,
            error: error.message,
            revisionRolledBack: oldStreamRevision,
            streamKey
          });
        });

        // Persist any local values the merge discarded (server won) as an offline
        // analytics event — a breadcrumb for recovery/audit.
        if (crdtDiscarded.length) {
          trackEvent('document.crdt_discarded', {
            document_id: document.id,
            template_id: document.template_id,
            status: document.status,
            discarded_values: Object.fromEntries(crdtDiscarded.map(({ field, value }) => [field, value])),
          }).catch((err) => logger.warn('Failed to track document.crdt_discarded', err));
        }
      }
    },
    onError: (error) => {
      logger.error('WebSocket connection error', {
        documentId: document?.id,
        error: error.message
      });
    }
  });

  // Log connection status changes
  useEffect(() => {
    if (wsConnected) {
      logger.info('WebSocket connected for document', {
        documentId: document?.id
      });
    }
  }, [wsConnected, document?.id]);

  // Auto-save refs
  const lastSavedDataRef = useRef<Record<string, unknown> | null>(null); // Track last saved form data (object)
  const currentFormDataRef = useRef<Record<string, unknown> | null>(null); // Track current form data for save-on-unmount
  const isDirtyRef = useRef(false); // Dirty flag — set by handleFormDataChange, cleared after save
  const dirtyFieldsRef = useRef<Set<string>>(new Set()); // Slugs with unsaved USER edits; incoming server updates skip exactly these fields
  const isSavingRef = useRef(false); // True while an async save is in flight
  const documentIdRef = useRef<number | string | null>(null); // Track document ID for save-on-unmount
  const lastDocumentDataRef = useRef<Record<string, unknown> | null>(null); // Track last known document.super_form from server (object)
  const lastRevisionRef = useRef<string | null>(null); // Track our own last-saved revision to suppress WebSocket echo loops
  const appliedRevisionByStreamRef = useRef<Record<string, string | null>>({}); // Last revision applied per stream (main/ssn/medicare/medicaid)
  const lastAppliedServerDataRef = useRef<Record<string, unknown> | null>(null); // Track the last data we received from server to prevent saving it back

  // Scroll position tracking
  const scrollPositions = useRef<Record<string, number>>({});
  const scrollRefs = useRef<Record<string, HTMLDivElement | null>>({
    document: null,
    summary: null
  });

  // Update formData when document changes from server (WebSocket pull)
  // This keeps the form in sync with real-time updates
  useEffect(() => {
    if (!document || !formDataInitialized) {
      return;
    }

    // Check if the document's super_form has changed from what we currently have locally
    const serverFormData = document.super_form || {};
    const currentData = currentFormDataRef.current || {};

    // Skip if server data matches current data (no update needed)
    if (isEqual(serverFormData, currentData)) {
      logger.debug('Server data matches current data, skipping update', {
        documentId: document.id
      });
      return;
    }

    // Per-field apply: every incoming field is merged into the form EXCEPT the
    // ones the user has edited locally and not yet saved (dirtyFieldsRef).
    // This replaces the old all-or-nothing gate where ANY unsaved edit dropped
    // the ENTIRE update — one dirty field no longer blocks live updates to the
    // rest of the document. Fields being saved right now are still in
    // dirtyFieldsRef (cleared only on save success), so an update racing an
    // in-flight save cannot clobber the outgoing values either.
    const { patch, skipped } = buildIncomingPatch(serverFormData, currentData, dirtyFieldsRef.current);

    if (skipped.length > 0) {
      logger.info('Holding back locally-edited fields from server update', {
        documentId: document.id,
        heldBack: skipped.slice(0, 10),
        heldBackCount: skipped.length,
      });
    }

    if (Object.keys(patch).length === 0) {
      logger.debug('Server update carries no applicable field changes', {
        documentId: document.id,
        heldBackCount: skipped.length,
      });
      return;
    }

    logger.info('Applying CRDT-merged server update', {
      documentId: document.id,
      revision: document.revision,
      totalFields: Object.keys(serverFormData).length,
      fieldsApplied: Object.keys(patch).length,
      fieldsHeldBack: skipped.length,
      hasCrdtMetadata: Boolean(document.super_form_meta),
      metadataFieldCount: document.super_form_meta ? Object.keys(document.super_form_meta).length : 0,
      sampleAppliedFields: Object.keys(patch).slice(0, 5)
    });

    // Per-key bookkeeping: only APPLIED keys advance the snapshots. Held-back
    // keys keep their previous lastSaved values so the per-field dirty diff
    // (and executeSave's "matches last server sync" short-circuit) stay truthful.
    currentFormDataRef.current = { ...currentData, ...patch };
    lastAppliedServerDataRef.current = { ...(lastAppliedServerDataRef.current || {}), ...patch };
    lastSavedDataRef.current = { ...(lastSavedDataRef.current || {}), ...patch };
    lastDocumentDataRef.current = serverFormData;

    // mergeFormData patches only these keys in the rendered store and notifies
    // subscribers without re-firing onChange (documented store contract) —
    // untouched fields, including the user's in-progress edits, keep their state.
    formDataActionsRef.current?.mergeFormData(patch);
  }, [document?.super_form, formDataInitialized]);

  // Check if data is still loading
  // Note: template will be null (not undefined) if no template is needed or template doesn't exist.
  // formDataInitialized is included so we don't render FormDataStoreProvider before the init effect
  // seeds initialFormDataRef.current — the provider's useRef captures the prop on first render and
  // ignores subsequent changes, so mounting with `{}` would lock the store at empty.
  const loading = clientLoading || document === undefined ||
    (templateId && template === undefined) ||
    !formDataInitialized;

  // Additional safety check - don't render if we have document but no template when one is expected
  const hasRequiredData = client !== undefined && document !== undefined &&
    (document?.template_id ? template !== undefined : true);

  const showSummaryTab = template?.show_summary === true;

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Initialize form data when document loads - only run once
  useEffect(() => {
    // Only initialize if we haven't already and document exists
    if (document && !formDataInitialized) {
      const initialFormData = document.super_form || {};

      // Clean up old-style formula field keys (field name instead of attribute name)
      // This handles documents saved before the attribute naming fix
      const cleanedFormData = { ...initialFormData };

      // Remove any keys that match formula field names but aren't proper attribute names
      // Formula fields (like need scores) should use pattern: person.assessments_need_scores.#{scope}.#{slug}.score
      if (template?.super_form?.definition?.form) {
        const checkAndRemoveOldKeys = (formConfig: Record<string, unknown>) => {
          Object.entries(formConfig).forEach(([fieldName, fieldConfig]) => {
            if (fieldConfig && typeof fieldConfig === 'object') {
              const config = fieldConfig as Record<string, unknown>;

              // If this field has a formula, remove the simple field name key
              if (config.formula) {
                if (fieldName in cleanedFormData) {
                  console.log(`Removing old-style formula field key: ${fieldName}`);
                  delete cleanedFormData[fieldName];
                }
              }

              // Recursively check nested fields in FieldGroups
              if (config.type === 'FieldGroup' && config.fields && typeof config.fields === 'object') {
                checkAndRemoveOldKeys(config.fields as Record<string, unknown>);
              }
            }
          });
        };

        checkAndRemoveOldKeys(template.super_form.definition.form);
      }

      // Seed the FormDataStoreProvider's initial snapshot, mirror into the
      // save-path ref, and flip the gate that mounts the provider on the
      // next render.
      initialFormDataRef.current = cleanedFormData;
      currentFormDataRef.current = cleanedFormData;
      setFormDataInitialized(true);
      lastSavedDataRef.current = cleanedFormData;
      lastDocumentDataRef.current = cleanedFormData;
      // Seed the applied-server snapshot too: SuperForm's mount-time formula
      // echoes re-emit these exact values through onChange, and isUserEdit
      // needs the snapshot to recognize them as echoes rather than user edits
      // (belt-and-braces alongside the source: 'engine' tag).
      lastAppliedServerDataRef.current = cleanedFormData;
      dirtyFieldsRef.current.clear();
      documentIdRef.current = document.id ?? null; // Track ID for save-on-unmount
    }
  }, [document, formDataInitialized, template]);

  // Clear dirty flag after form initialization settles.
  // SuperForm's initial formula calculations call onChange on mount, which sets
  // isDirtyRef. These aren't real user edits and shouldn't block WebSocket updates.
  // The setTimeout(0) is needed because SuperForm's formula mount effect also uses
  // setTimeout(0) to batch formula recalculations — our clear must run after those
  // have flushed, not before.
  useEffect(() => {
    if (formDataInitialized) {
      const timer = setTimeout(() => {
        isDirtyRef.current = false;
        dirtyFieldsRef.current.clear();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [formDataInitialized]);

  // Refresh document and template from server when loading to ensure we have latest data
  useEffect(() => {
    if (document && isOnline && !isDraftRoute && documentIdNum && clientId) {
      pullDocument(clientId, documentIdNum).catch(err => {
        logger.error('Failed to refresh document on load', { documentId: documentIdNum, error: err });
      });
      if (document.template_id) {
        pullTemplate(document.template_id).catch(err => {
          logger.error('Failed to refresh template on document load', { templateId: document.template_id, error: err });
        });
      }
    }
  }, [document?.id, document?.template_id, isOnline, isDraftRoute, documentIdNum, clientId]);

  // Error handling
  useEffect(() => {
    // Check for valid document ID or local ID (at least one must be present)
    if (!isDraftRoute && (!documentIdNum || isNaN(documentIdNum))) {
      setError('Invalid document ID');
      return;
    }
    if (isDraftRoute && !localId) {
      setError('Invalid draft ID');
      return;
    }

    if (client === null && !loading) {
      setError('Client not found or access denied');
    } else if (document === null && !loading) {
      setError('Document not found');
    } else {
      setError(null);
    }
  }, [documentIdNum, localId, isDraftRoute, client, document, loading]);

  // Restrict non-completed documents when template doesn't support offline use
  useEffect(() => {
    if (loading || !document || template === undefined) return;
    if (!templateOfflineEnabled && !isComplete) {
      setError('This document template does not support offline editing. Please use the web application to edit this document.');
    }
  }, [loading, document, template, templateOfflineEnabled, isComplete]);

  // Store scroll position when switching tabs
  const storeScrollPosition = useCallback((tab: DocumentTab) => {
    if (scrollRefs.current[tab]) {
      scrollPositions.current[tab] = scrollRefs.current[tab]!.scrollTop;
    }
  }, []);

  // Restore scroll position when tab becomes active
  const restoreScrollPosition = useCallback((tab: DocumentTab) => {
    if (scrollRefs.current[tab] && scrollPositions.current[tab] !== undefined) {
      scrollRefs.current[tab]!.scrollTop = scrollPositions.current[tab];
    }
  }, []);

  // Handle form data changes
  const handleFormDataChange = useCallback((fieldName: string, value: unknown, meta?: FormChangeMeta) => {
    // FormDataStoreProvider has already written the value into its internal ref
    // by the time this callback fires; we just mirror into our save-path ref
    // and flag dirty so the debounced autosave picks it up.
    currentFormDataRef.current = { ...(currentFormDataRef.current || {}), [fieldName]: value };

    // Only genuine user edits mark the document dirty. Engine-driven writes
    // (formula backfill/cascade, tagged source: 'engine' by super-form) and
    // echoes of a just-applied server snapshot fire with no user interaction;
    // marking dirty for them wedges the flag — saves are blur-driven, no blur
    // ever comes, and the server-sync effect then drops every live update.
    if (isUserEdit(meta, lastAppliedServerDataRef.current, fieldName, value)) {
      isDirtyRef.current = true;
      dirtyFieldsRef.current.add(fieldName);
    }

    // Any edit invalidates the previous finalize attempt's fix-elsewhere banner;
    // the next Finish re-runs finalize and re-surfaces anything still outstanding.
    setExternalErrors(prev => (prev.length > 0 ? [] : prev));

    // Dismiss validation errors when a field changes from blank.
    // Match both exact field name and dotted source paths ending with the field name
    // (e.g. fieldName "zip" matches error field "person.client.address.zip").
    if (isPresent(value)) {
      const matchesField = (errorField?: string) =>
        errorField === fieldName || (errorField != null && errorField.endsWith(`.${fieldName}`));

      setFinishBlockers(prev => prev.length > 0 ? prev.filter(b => !matchesField(b.field)) : prev);
      setValidationErrors(prev => {
        const keysToRemove = Object.keys(prev).filter(k => matchesField(k));
        if (keysToRemove.length === 0) return prev;
        const next = { ...prev };
        keysToRemove.forEach(k => delete next[k]);
        return next;
      });
    }
  }, []);

  // Open notes panel to a specific field
  const handleOpenNotesToField = useCallback((fieldSlug: string, fieldLabel?: string) => {
    setFocusedNoteField(fieldSlug);
    setFocusedNoteFieldLabel(fieldLabel);
  }, []);

  // Confirm a prefilled field. The Dexie write is idempotent and rides the
  // existing CRDT pipeline via the `confirmed_prefill:<slug>` super_form_meta namespace.
  const handlePrefillConfirmed = useCallback((fieldSlug: string) => {
    const docId = document?.id;
    if (!docId) return;
    void confirmPrefill(docId, fieldSlug).catch((err) => {
      logger.error('Failed to confirm prefill', { documentId: docId, fieldSlug, err });
    });
  }, [document?.id]);

  // Batch variant for row-level / section-level confirms — written in a single
  // Dexie update so concurrent reads don't clobber each other.
  const handlePrefillsConfirmed = useCallback((fieldSlugs: string[]) => {
    const docId = document?.id;
    if (!docId || fieldSlugs.length === 0) return;
    void confirmPrefills(docId, fieldSlugs).catch((err) => {
      logger.error('Failed to batch-confirm prefills', { documentId: docId, count: fieldSlugs.length, err });
    });
  }, [document?.id]);

  // A depends_on edit re-revealed fields whose preserved answers the user can
  // now see. Re-stamp their HLCs so those answers compete in CRDT merges as of
  // the gating edit, not as of when they were originally typed. SuperForm
  // reports editor-originated reveals, blanks included — the blank policy
  // (only stamped blanks re-assert) lives in stampRevealedFields.
  const handleFieldsRevealed = useCallback((fields: Record<string, unknown>) => {
    const docId = document?.id;
    if (!docId) return;
    void stampRevealedFields(docId, Object.keys(fields)).catch((err) => {
      logger.error('Failed to re-stamp revealed fields', { documentId: docId, count: Object.keys(fields).length, err });
    });
  }, [document?.id]);

  // @mes-amis/super-form <= 1.2.25 (the current dep) doesn't declare
  // onFieldsRevealed — it ships with 1.2.26 — so the spread keeps the compile
  // independent of the package bump; inline the prop once the bump lands.
  const revealedFieldsProps = { onFieldsRevealed: handleFieldsRevealed };

  const prefillResolvers = useMemo(
    () => buildPrefillFormResolvers(
      template?.super_form?.definition?.form as Record<string, any> | undefined,
      template?.super_form?.definition?.fields as Record<string, any> | undefined
    ),
    [template?.super_form?.definition?.form, template?.super_form?.definition?.fields]
  );

  // Set of slugs the user has interacted with — derived from `super_form_meta`'s
  // `confirmed_prefill:<slug>` keys. This is the SOLE confirmation signal for
  // the client (tint clears, dot hides, idempotent confirm). Server-emitted
  // pending Array stays separate (drives banner/section counts).
  const confirmedPrefillsSet = useMemo<ReadonlySet<string>>(() => {
    const meta = document?.super_form_meta || {};
    const set = new Set<string>();
    for (const k of Object.keys(meta)) {
      if (k.startsWith('confirmed_prefill:')) {
        set.add(k.slice('confirmed_prefill:'.length));
      }
    }
    return set;
  }, [document?.super_form_meta]);

  // `visiblePrefills` used to live at this top level as a useMemo depending on
  // `formData` (React state). After the FormDataStoreProvider refactor
  // (PR #193) `formData` no longer lives in state — it's owned by the store —
  // so `PrefillProviderFromStore` (defined above) derives it INSIDE
  // FormDataStoreProvider. The pending pre-fill sections the issue controls
  // list come from that PrefillProvider (the package's IssuesProvider reads them).

  // Carry the conflict-preserved edits into a fresh draft (shared with the
  // documents list's locked-online chip flow), then open it.
  const handleCreateFromConflict = useCallback(async (doc: Document) => {
    if (!clientId) return;
    const localId = await createDraftFromLockedOnlineConflict(doc, template ?? undefined, clientId, currentPerson);
    setShowLockedOnlineModal(false);
    navigate(`/clients/${clientSlug}/documents/drafts/${localId}/edit`);
  }, [template, clientId, currentPerson, clientSlug, navigate]);

  // Clear focused note field (stable reference for child components)
  const handleFocusFieldCleared = useCallback(() => {
    setFocusedNoteField(null);
    setFocusedNoteFieldLabel(undefined);
  }, []);

  // --- Debounced save ---
  // Batches rapid blur events (e.g. tabbing through fields) into a single
  // save. The actual save runs 300ms after the last blur, keeping the UI
  // responsive during quick navigation.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savePromiseRef = useRef<Promise<void> | null>(null);

  const executeSave = useCallback((): Promise<void> | undefined => {
    const currentData = currentFormDataRef.current;
    const docId = document?.id;
    if (!currentData || !docId) return;

    if (isFormReadonly || isFinishing) return;
    if (!templateOfflineEnabled) return;
    if (document?.sync_error_type === 'deleted_online') return;
    if (!isDirtyRef.current) return;

    // Don't save if this data came from a server sync (not a user edit)
    if (lastAppliedServerDataRef.current && isEqual(currentData, lastAppliedServerDataRef.current)) {
      logger.debug('Skipping save on blur - data matches last server sync', {
        documentId: docId
      });
      lastAppliedServerDataRef.current = null;
      isDirtyRef.current = false;
      dirtyFieldsRef.current.clear();
      return;
    }

    isSavingRef.current = true;
    setAutoSaving(true);
    const saving = updateDocument(docId, { super_form: currentData })
      .then((result) => {
        // A field's edit is now saved unless the user changed it again while
        // the save was in flight — those keys stay dirty for the next save.
        for (const key of Array.from(dirtyFieldsRef.current)) {
          if (isEqual(currentFormDataRef.current?.[key], currentData[key])) {
            dirtyFieldsRef.current.delete(key);
          }
        }
        isDirtyRef.current = dirtyFieldsRef.current.size > 0;

        if (result && 'revision' in result && typeof result.revision === 'string') {
          lastRevisionRef.current = result.revision;

          if (result.synced) {
            logger.debug('Stored revision from save response', {
              documentId: docId,
              revision: result.revision
            });
          }
        }

        // Server acknowledged (and possibly CRDT-normalized) state. Merge its
        // returned form per-field, holding back keys the user edited mid-flight
        // so a slow save response cannot clobber newer typing.
        if (result?.super_form) {
          lastSavedDataRef.current = result.super_form;
          const { patch } = buildIncomingPatch(
            result.super_form,
            currentFormDataRef.current || {},
            dirtyFieldsRef.current,
          );
          if (Object.keys(patch).length > 0) {
            currentFormDataRef.current = { ...(currentFormDataRef.current || {}), ...patch };
            lastAppliedServerDataRef.current = { ...(lastAppliedServerDataRef.current || {}), ...patch };
            formDataActionsRef.current?.mergeFormData(patch);
          }
        } else {
          lastSavedDataRef.current = currentData;
        }

        trackEvent('document.edited', {
          document_id: docId,
          client_id: clientId ?? undefined,
          template_id: document?.template_id,
          status: document?.status,
          trigger: 'autosave',
        }).catch(err => {
          logger.error('Failed to track document.edited event', { documentId: docId, error: err });
        });
      })
      .catch(err => {
        // Re-mark dirty so the next save attempt includes these changes
        isDirtyRef.current = true;
        logger.error('Auto-save failed', { documentId: docId, error: err });
        setWarning('Auto-save failed. Your changes may not be saved.');
      })
      .finally(() => {
        isSavingRef.current = false;
        setAutoSaving(false);
      });
    savePromiseRef.current = saving;
    return saving;
  }, [document?.id, isFormReadonly, isFinishing, templateOfflineEnabled]);

  // Writes the answers still waiting for the debounced save, and waits for one in
  // flight, so their HLC stamps come before a warning dismissal stamped next.
  const flushPendingSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
      executeSave();
    }
    await savePromiseRef.current;
  }, [executeSave]);

  const saveFormData = useCallback(() => {
    // Cancel any pending debounced save — a newer snapshot is available
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    // Batch: wait 300ms for more changes before actually saving
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      executeSave();
    }, 300);
  }, [executeSave]);

  // Flush pending save on unmount so data isn't lost.
  // If document.id is already gone (navigation cleanup), executeSave will
  // early-return safely. The existing save-on-unmount via documentIdRef
  // (elsewhere in this file) handles that edge case.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        if (!isSavingRef.current) executeSave();
      }
    };
  }, [executeSave]);

  // Apply a single-field change that originated outside SuperForm's own
  // plumbing (notes panel, etc.). SuperForm's field path writes through to
  // the store before calling the host's onChange, so handleFormDataChange
  // alone is sufficient there. External callers must explicitly push into
  // the store via mergeFormData first, otherwise useFieldValue / useFormData
  // subscribers (NotesProviderFromStore, field-level note indicators) stay
  // stale until a server roundtrip re-applies the snapshot. Regression
  // covered by cypress/component/DocumentEdit-note-store-sync.cy.tsx.
  //
  // Load-bearing contract from @mes-amis/super-form 0.2.x: mergeFormData
  // updates the store's ref and notifies subscribers but does NOT invoke
  // the provider's onChange. That's why this helper calls
  // handleFormDataChange explicitly after the store write — and why the
  // two calls together aren't a double-fire. If a future super-form
  // release makes mergeFormData fire onChange, handleFormDataChange will
  // run twice per external change. Its body is idempotent
  // (currentFormDataRef gets the same write, isDirtyRef stays true,
  // validation dismissal is a no-op on second pass), so the regression
  // would be cost-only, not correctness, but it's worth knowing.
  const applyFieldChange = useCallback((fieldName: string, value: unknown) => {
    formDataActionsRef.current?.mergeFormData({ [fieldName]: value });
    handleFormDataChange(fieldName, value);
  }, [handleFormDataChange]);

  // Range-checking lives in DateCompletedInput (@mes-amis/super-form >= 1.2.45),
  // which owns the field and its min/max window: an out-of-range value is
  // reported next to the input and never reaches this callback.
  const handleRecordedAtChange = useCallback(async (value: string | null) => {
    if (!document?.id) return;
    await updateDocument(document.id, { recorded_at: value ?? null } as Partial<Document>);
  }, [document?.id]);

  // Handle note typing — updates formData state only (no persist)
  const handleNoteChange = useCallback((scope: string | null, detail: string) => {
    const key = superFormKeyForNote(scope);
    applyFieldChange(key, detail);
  }, [applyFieldChange]);

  // Handle note blur — persists to IndexedDB (and server via CRDT pipeline)
  // formData is already up-to-date from handleNoteChange calls during typing
  const handleNoteSave = useCallback(() => {
    saveFormData();
  }, [saveFormData]);

  // Handle note deletion — clears the value in super_form and persists
  const handleNoteDelete = useCallback((scope: string | null) => {
    const key = superFormKeyForNote(scope);
    applyFieldChange(key, '');
    saveFormData();
  }, [applyFieldChange, saveFormData]);

  // Handle tab change
  const handleTabChange = useCallback((newTab: DocumentTab) => {
    // Store current tab's scroll position before switching
    storeScrollPosition(activeTab);
    setActiveTab(newTab);
    if (newTab === 'summary') setSummaryVisited(true);

    // Restore new tab's scroll position after a brief delay to allow tab content to render
    setTimeout(() => {
      restoreScrollPosition(newTab);
    }, 50);
  }, [activeTab, storeScrollPosition, restoreScrollPosition]);

  // Navigate from summary to a specific field in the form
  const handleSummaryFieldClick = useCallback((fieldName: string) => {
    storeScrollPosition(activeTab);
    setActiveTab('document');

    // Wait for the document tab to render, then scroll to the field
    setTimeout(() => {
      const el = window.document.getElementById(`field-${fieldName}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Brief highlight to draw attention to the field
        el.classList.add('ring-4', 'ring-blue-400', 'ring-offset-4', 'rounded-lg');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-blue-400', 'ring-offset-4', 'rounded-lg');
        }, 2000);
      }
    }, 100);
  }, [activeTab, storeScrollPosition]);

  // The name the current user must type to sign a title slot.
  const currentPersonName = useMemo(() => officialFullName(currentPerson), [currentPerson]);

  // The soft-validation dismissals that still apply, in the package's shape; one
  // recorded here and not yet synced shows the local person's name.
  const warningDismissals = useMemo(
    () => toEditorWarningDismissals(
      document?.warning_dismissals,
      currentPersonName,
      template?.super_form?.definition as SuperFormDefinition | undefined,
      document?.super_form_meta,
    ),
    [document?.warning_dismissals, currentPersonName, template?.super_form?.definition, document?.super_form_meta],
  );

  // Ignore / Undo a warning: written to the local document (pending until the
  // server accepts it, so it survives going offline) and sent right away when online.
  const pushDismissal = useCallback((docId: number | string) => {
    if (!navigator.onLine) return;
    void dispatchDataEvent(DataEvents.SYNC_DOCUMENT, { documentId: docId }).catch((err) => {
      logger.warn('Failed to sync a warning dismissal', { documentId: docId, err });
    });
  }, []);

  // The answers the modal just saved (or still debounced) are stamped first: stamped
  // after the dismissal, they would re-open the warning it ignores.
  const handleWarningDismissed = useCallback((key: string, justification: string, fields: string[]) => {
    const docId = document?.id;
    if (!docId) return;
    flushPendingSave()
      .then(() => dismissWarning(docId, key, justification, currentPersonName, fields))
      .then(() => pushDismissal(docId))
      .catch((err) => logger.error('Failed to dismiss warning', { documentId: docId, key, err }));
  }, [document?.id, currentPersonName, pushDismissal, flushPendingSave]);

  const handleWarningUndismissed = useCallback((key: string) => {
    const docId = document?.id;
    if (!docId) return;
    undismissWarning(docId, key)
      .then(() => pushDismissal(docId))
      .catch((err) => logger.error('Failed to undo a warning dismissal', { documentId: docId, key, err }));
  }, [document?.id, pushDismissal]);

  const blockers = useMemo<IssueBlocker[]>(
    () => [...finishBlockers, ...externalErrors.map(externalErrorBlocker)],
    [finishBlockers, externalErrors],
  );

  const issueControls = usesFloatingIssueControls(currentPerson, template?.super_form?.definition);

  // The error banner's list: field and finalize errors, then the sections with
  // unconfirmed pre-fills. External errors get their own banner.
  const finishErrors = useMemo<NavigableError[]>(
    () => [
      ...finishBlockers.map(({ kind, field, label, message }) => (kind === 'field' ? { field, label, message, kind } : { label: '', message })),
      ...pfSectionErrors,
    ],
    [finishBlockers, pfSectionErrors],
  );

  // Local 'requested' signature rows built from the template config — the offline
  // stand-in for the rows the server creates on finalize. The sync layer matches
  // them by local_id when it replays the finalize online.
  const buildLocalSignatureRows = useCallback((docId: number | string): Signature[] => {
    const config = template?.signature_config;
    if (!config) return [];

    const rows: Signature[] = [];
    for (const title of config.required_titles || []) {
      rows.push({
        local_id: `${SIGNATURE_ID_PREFIX.TITLE}${title.id}`,
        document_id: docId,
        status: 'requested',
        title: title as Signature['title'],
      });
    }
    for (const person of config.required_people || []) {
      rows.push({
        local_id: `${SIGNATURE_ID_PREFIX.PERSON}${person.id}`,
        document_id: docId,
        status: 'requested',
        signatory: { id: person.id, first_name: person.first_name, last_name: person.last_name },
        signatory_id: person.id,
      });
    }
    return rows;
  }, [template?.signature_config]);

  // A finalize left the document waiting_for_signature: seed the signature slots
  // (from the server, which creates them on finalize, or from the template config
  // when offline) and move to the Signature tab. The status keeps the form read-only.
  const enterSigningStage = useCallback(async (doc: Document) => {
    const docId = doc.id;
    if (!docId) return;

    const existing = await getLocalSignatures(docId);
    if (existing.length === 0) {
      let seeded = false;
      if (navigator.onLine && typeof docId === 'number' && clientId) {
        try {
          const rows = await fetchSignatures(clientId, docId);
          if (rows.length > 0) {
            await storeServerSignatures(docId, rows);
            seeded = true;
          }
        } catch (err) {
          logger.warn('Could not fetch signatures after finalize; falling back to template config', { documentId: docId, error: err });
        }
      }
      if (!seeded) {
        await createSignatures(docId, buildLocalSignatureRows(docId));
      }
    }

    setActiveTab('signature');
  }, [clientId, buildLocalSignatureRows]);

  // Finalize document
  const handleFinish = useCallback(async () => {
    if (isFinishing) return;
    setFinishBlockers([]);
    setPfSectionErrors([]);
    setExternalErrors([]);
    setServerWarnings([]);

    const docId = document?.id;
    if (!docId) {
      setError('Cannot finish: document not loaded');
      return;
    }

    try {
      setIsFinishing(true);

      const latestFormData = currentFormDataRef.current || {};
      // First, save the latest state to the server if online
      // Track the effective document ID — sync may replace the local UUID with a server numeric ID
      let effectiveDocId: number | string = docId;

      if (isOnline) {
        const result = await updateDocument(docId, {
          super_form: latestFormData,
          updated_at: new Date().toISOString()
        });

        // Store revision if available
        if (result && 'revision' in result && typeof result.revision === 'string') {
          lastRevisionRef.current = result.revision;
        }

        // Wait for sync to complete — sync may assign a new server ID to draft documents
        // force: this is a user-initiated save, so send even a payload the
        // sweep is holding — they may well have just fixed what was wrong.
        const syncedDoc = await dispatchDataEvent<typeof DataEvents.SYNC_DOCUMENT, Document | null>(DataEvents.SYNC_DOCUMENT, { documentId: docId, force: true });
        if (syncedDoc?.id !== undefined && syncedDoc.id !== docId) {
          effectiveDocId = syncedDoc.id;
        }
      } else {
        // If offline, just update locally
        const result = await updateDocument(docId, {
          super_form: latestFormData,
          updated_at: new Date().toISOString()
        });

        // Store revision if available
        if (result && 'revision' in result && typeof result.revision === 'string') {
          lastRevisionRef.current = result.revision;
        }
      }

      // Then finalize the document (calls /finalize endpoint). The status
      // transition is owned by the server (or the offline fallback): 'in_review'
      // when the person's Finish goes to a reviewer, 'completed' when no
      // signatures are required, 'waiting_for_signature' when the template has
      // a signature config.
      const finalized = await finalizeDocument(effectiveDocId, latestFormData);

      // Track document finalization (the user clicked "Finish" and the doc was finalized)
      // Use effectiveDocId — the local UUID may have been replaced with a server
      // numeric id during sync above. On draft-route finalize, documentIdNum is
      // null because the URL carries a localId rather than a numeric document id.
      trackEvent('document.finalized', {
        document_id: effectiveDocId,
        client_id: clientId ?? undefined,
        template_id: document?.template_id,
        status: finalized?.status || 'complete',
      }).catch(err => {
        logger.error('Failed to track document.finalized event', { documentId: effectiveDocId, error: err });
      });

      if (finalized?.status === 'waiting_for_signature') {
        // Signing window: stay in the editor and collect signatures in the Signature tab
        await enterSigningStage(finalized);
        return;
      }

      // Navigate back to client page
      navigate(`/clients/${clientSlug}`);
    } catch (err) {
      logger.error('Failed to finish document', { documentId: docId, error: err });
      const validationErrors = (err as Error & { validationErrors?: Record<string, string[]> }).validationErrors;
      const fieldErrors = (err as Error & { fieldErrors?: ServerFieldErrors }).fieldErrors;
      const externalErrors = (err as Error & { externalErrors?: ExternalError[] }).externalErrors;
      const warnings = (err as Error & { warnings?: SoftValidationWarning[] }).warnings;
      if (externalErrors && externalErrors.length > 0) {
        setExternalErrors(externalErrors);
      }
      if (warnings && warnings.length > 0) {
        setServerWarnings(warnings);
      }

      const finishFailure = (message: string, index = 0): IssueBlocker => ({ id: `system:${index}`, kind: 'system', label: 'Could not finish', message });
      if (fieldErrors && Object.keys(fieldErrors).length > 0) {
        applyServerFieldErrors(fieldErrors);
      } else if (warnings && warnings.length > 0) {
        // The warnings are the refusal; the flat messages only repeat them.
      } else if (validationErrors) {
        // Fallback: parse flat error messages (no field navigation)
        const messages = Object.values(validationErrors)
          .flat()
          .flatMap((msg: string) => msg.split(',').map(s => s.trim()))
          .filter(msg => msg.length > 0);
        const unique = [...new Set(messages)];
        setFinishBlockers(unique.length > 0
          ? unique.map((msg, index) => finishFailure(msg, index))
          : [finishFailure('Validation failed. Please check the form and try again.')]);
      } else if (!externalErrors || externalErrors.length === 0) {
        setFinishBlockers([finishFailure('Failed to complete document. Please try again.')]);
      }
      // The rejected finalize's issues are listed in the (re)opened issues menu.
      issuesActionsRef.current?.openMenu();
    } finally {
      setIsFinishing(false);
    }
  }, [document?.id, document?.template_id, isFinishing, isOnline, navigate, clientId, clientSlug, enterSigningStage, applyServerFieldErrors]);

  // Validate form before finishing
  const validateBeforeFinish = useCallback(() => {
    if (!template?.super_form?.definition?.fields) {
      handleFinish();
      return;
    }

    // Clear the fix-elsewhere errors and the server's warnings too: they're a
    // snapshot of the last finalize attempt. If this validation pass
    // short-circuits before handleFinish (which is where it would otherwise be
    // cleared), stale ones must not linger next to the fresh local errors.
    setExternalErrors([]);
    setServerWarnings([]);
    const errors = validateFormData(
      currentFormDataRef.current || {},
      template.super_form.definition.fields as Record<string, any>,
      template.super_form.definition.form as Record<string, any>
    );
    const errorObject = validationErrorsToObject(errors);

    setValidationErrors(errorObject);
    // validateFormData follows the fields map; the stepper walks the form top to bottom.
    setFinishBlockers(errors
      .map(e => ({ id: e.field, kind: 'field' as const, field: e.field, label: e.label, message: e.message }))
      .sort((a, b) => prefillResolvers.resolveOrder(a.field) - prefillResolvers.resolveOrder(b.field)));

    // Re-evaluates every soft-validation rule, switches the issue controls to
    // their after-Finish view and opens the issues menu. Without them, the error
    // banner lists one entry per section with outstanding PARC slugs; the
    // chevron-nav scrolls to the `__confirm__<sectionId>` anchor rendered by
    // PrefillSectionPill. Pending pre-fills block Finish either way; so do open warnings.
    let outstanding = { warnings: 0, prefills: 0 };
    if (issueControls) {
      outstanding = issuesActionsRef.current?.submit() ?? outstanding;
    } else {
      const pendingSet = computeUnconfirmedPrefills(document?.field_prefills, confirmedPrefillsSet, prefillResolvers, currentFormDataRef.current || {});
      const sectionErrors = buildPfSectionErrors(template.super_form.definition.form as Record<string, unknown>, pendingSet);
      setPfSectionErrors(sectionErrors);
      outstanding = { warnings: 0, prefills: sectionErrors.length };
    }

    if (errors.length === 0 && outstanding.warnings === 0 && outstanding.prefills === 0) {
      handleFinish();
    }
  }, [template, handleFinish, issueControls, document?.field_prefills, confirmedPrefillsSet, prefillResolvers]);

  // All slots are signed: refresh the document (the server flips the status to
  // 'completed' when the last required signature lands) and leave the editor.
  const finishAfterSignatures = useCallback(async (docId: number | string) => {
    try {
      setIsFinishing(true);
      if (isOnline && typeof docId === 'number' && clientId) {
        await pullDocument(clientId, docId);
      }
    } catch (err) {
      logger.error('Failed to refresh document after signing', { documentId: docId, error: err });
    } finally {
      setIsFinishing(false);
    }
    navigate(`/clients/${clientSlug}`);
  }, [isOnline, clientId, navigate, clientSlug]);

  // Sign one slot in the Signature tab. Slots the server knows about PATCH the
  // signature endpoint (the document status transitions server-side); offline
  // slots are captured locally and replayed by the sync layer. After the last
  // slot, the document is complete and we leave the editor.
  // `signOptions` arrives from the ClientProxy slot's signer identity question;
  // the chosen representative resolves to the person whose exact name the
  // signature must match (client, legal rep, or the current user on-behalf).
  const handleTabSign = useCallback(async (slotId: number | string, name: string, signOptions?: SignOptions) => {
    const docId = document?.id;
    const row = (signatures || []).find(sig => (sig.id ?? sig.local_id) === slotId);
    if (!docId || !row) return { ok: false, error: 'Signature not found. Please try again.' };

    let slotOptions: SignSlotOptions | undefined;
    const representative = signOptions?.client_signature_representative;
    // Resolve from the same candidate set the card displayed: the row's
    // server-provided one when present, the locally derived one otherwise.
    const candidates = row.representative_signing ?? representativeSigning;
    if (representative && candidates) {
      const candidate = representative === 'myself'
        ? candidates.client
        : representative === 'legal_rep'
          ? candidates.legal_representative
          : currentPerson?.id
            ? { person_id: currentPerson.id, official_full_name: currentPersonName }
            : null;
      if (!candidate) return { ok: false, error: 'Could not determine the signatory. Please try again.' };
      slotOptions = {
        representative,
        signatory: { id: candidate.person_id, name: candidate.official_full_name },
        signingStatement: signOptions?.signing_statement,
      };
    }

    const result = await signSignatureSlot(docId, row, name, {
      online: isOnline,
      clientId,
      currentPersonId: currentPerson?.id,
      currentPersonName,
    }, slotOptions);
    if (!result.ok) return result;

    if (result.signedLocally) {
      // Mirror the server's signing strategy locally: partial → signing_in_progress,
      // all signed → completed. locally_finalized makes the sync layer replay the
      // finalize + signatures when back online; the server reconciles the status then.
      await updateDocument(docId, {
        status: result.allSigned ? 'completed' : 'signing_in_progress',
        locally_finalized: true,
        synced: false,
      });
    }

    if (result.allSigned) {
      await finishAfterSignatures(docId);
    }
    return { ok: true };
  }, [document?.id, signatures, currentPerson?.id, currentPersonName, isOnline, clientId, finishAfterSignatures, representativeSigning]);

  // Fallback slot order for rows without a server id (offline-built slots):
  // template config order, titles first then people — the sequence the server
  // creates slots in. Server-fetched rows sort by id instead (see
  // compareSignatureSlots), matching the online app's display order.
  const slotOrder = useMemo(() => {
    const order = new Map<string, number>();
    let index = 0;
    for (const title of template?.signature_config?.required_titles || []) {
      order.set(`${SIGNATURE_ID_PREFIX.TITLE}${title.id}`, index++);
    }
    for (const person of template?.signature_config?.required_people || []) {
      order.set(`${SIGNATURE_ID_PREFIX.PERSON}${person.id}`, index++);
    }
    return order;
  }, [template?.signature_config]);

  // Signature slots for the Signature tab, from the persisted rows (server rows
  // after an online finalize, template-config rows after an offline one). Slots
  // signed offline stay status 'requested' until synced — display them as
  // completed via local_completed.
  const signatureTabSlots = useMemo<DocumentSignature[]>(() => (
    [...(signatures || [])]
      .sort((a, b) => compareSignatureSlots(a, b, slotOrder))
      .map((sig) => ({
        id: sig.id ?? sig.local_id,
        status: sig.local_completed ? 'completed' as const : sig.status,
        name_confirmation: sig.name_confirmation,
        signed_at: sig.signed_at,
        completed_at: sig.completed_at ?? (sig.local_completed ? sig.signed_at : undefined),
        title: sig.title,
        signatory: sig.signatory
          ? { id: sig.signatory.id, official_full_name: officialFullName(sig.signatory) }
          // Unsigned title slots are signed by the current user — show their name
          : (sig.title ? { id: currentPerson?.id ?? 0, official_full_name: currentPersonName } : null),
        // Offline always permits signing regardless of title — a signer without
        // the required title can still capture the signature and finish the
        // document; the server enforces the title on sync and reports a
        // permission error there if it doesn't hold.
        can_sign: true,
        // The ClientProxy slot asks who is signing (client, legal rep, or on behalf).
        // Server-fetched rows carry the authoritative candidate set (correct
        // Caregiver/Client label); rows built offline fall back to the local one.
        representative_signing: sig.title?.name === 'ClientProxy'
          ? sig.representative_signing ?? representativeSigning
          : undefined,
        // Completed slots surface who provided the signature and any statement
        client_signature_representative: sig.client_signature_representative,
        signing_statement: sig.signing_statement,
      }))
  ), [signatures, slotOrder, currentPerson?.id, currentPersonName, representativeSigning]);

  // Cleanup on unmount - save any pending changes immediately
  useEffect(() => {
    return () => {
      // Completed documents are read-only — never save on unmount
      if (isCompleteRef.current) return;

      // Save any unsaved changes immediately on unmount
      const currentData = currentFormDataRef.current;
      const docId = documentIdRef.current;
      if (currentData && docId) {
        // Check if data has changed using deep equality check
        if (!isEqual(currentData, lastSavedDataRef.current)) {
          // Fire-and-forget save - will save to IndexedDB and sync to server
          updateDocument(docId, { super_form: currentData }).catch(err => {
            logger.error('Failed to save on unmount', { documentId: docId, error: err });
          });
        }
      }
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium mb-2">Loading document...</p>
          <p className="text-gray-600">Please wait while we fetch the document data</p>
        </div>
      </div>
    );
  }

  // Client not found or wrong organization
  if (client === null) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mx-auto h-12 w-12 text-red-400 mb-4">
            <ExclamationTriangleIcon />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Client Not Found</h2>
          <p className="text-gray-600 mb-4">The client you&apos;re looking for doesn&apos;t exist or you don&apos;t have access to it.</p>
          <Link
            to="/clients"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  // Error state (document not found, invalid ID, etc.)
  if (error) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="mx-auto h-12 w-12 text-red-400 mb-4">
            <ExclamationTriangleIcon />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Document</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link
            to={`/clients/${clientSlug}`}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
          >
            Back to Client
          </Link>
        </div>
      </div>
    );
  }

  return (
    <ErrorReporterProvider onError={reportSuperFormError}>
      <HelpDocumentProvider helpDocument={helpDocument || null}>
      <div className="h-full flex flex-col bg-gray-50">

        {/* Header */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0 relative z-50">
          <TitleBar
            onBack={() => window.history.length > 1 ? navigate(-1) : navigate(`/clients/${clientSlug}`)}
            {...(isPhone ? {} : metaProps)}
            actions={
              <>
                {/* Auto-save indicator (only show when editing) */}
                {!isFormReadonly && autoSaving && (
                  <div className="flex items-center text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span>Saving...</span>
                  </div>
                )}

                {/* Save and Exit / Exit button */}
                <Button
                  variant="borderless"
                  onClick={() => window.history.length > 1 ? navigate(-1) : navigate(`/clients/${clientSlug}`)}
                  size={isPhone ? 'md' : 'sm'}
                >
                  {isFormReadonly ? 'Exit' : 'Save & Exit'}
                </Button>

                {/* Finish button (only show while editable and template supports offline) */}
                {!isFormReadonly && templateOfflineEnabled && (
                  <Button
                    variant="primary"
                    onClick={validateBeforeFinish}
                    disabled={isFinishing}
                    size={isPhone ? 'md' : 'sm'}
                    className="me-4"
                  >
                    {isFinishing ? 'Finishing...' : finishesInReview ? 'Set as ready for review' : 'Finish'}
                  </Button>
                )}
              </>
            }
          />
          
          {(isDeletedOnline || isLockedOnline || isInReview || isChangesRequested || warning) && (
            <div className="px-6 py-4">

              {(isInReview || isChangesRequested) && !isLockedOnline && (
                <div className="mb-4 bg-gray-100 border border-gray-300 rounded-md p-4">
                  <div className="flex">
                    <InformationCircleIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">{isInReview ? 'In Review' : 'Changes Requested'}</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {isInReview
                          ? 'This document is in review on Mon Ami. Open it on the web to see comments or make changes.'
                          : 'Changes were requested on Mon Ami. Open it on the web to make them.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Warning Message */}
              {isDeletedOnline && (
                <div className="mb-4 bg-gray-100 border border-gray-300 rounded-md p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-gray-500 mr-2 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-800">Deleted Online</h3>
                      <p className="text-sm text-gray-600 mt-1">This document was deleted in the online app. You can view it here but editing is disabled. Use the document list to delete it locally or duplicate it.</p>
                    </div>
                  </div>
                </div>
              )}

              {lockedOnline && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-yellow-800">{lockedOnline.bannerTitle}</h3>
                      <p className="text-sm text-yellow-700 mt-1">{lockedOnline.bannerBody}</p>
                      <div className="mt-3">
                        <Button variant="secondary" onClick={() => setShowLockedOnlineModal(true)}>
                          Review edits
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {warning && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-yellow-800">Warning</h3>
                      <p className="text-sm text-yellow-700 mt-1">{warning}</p>
                    </div>
                  </div>
                </div>
              )}

            </div>
          )}

          <LockedOnlineConflictModal
            isOpen={showLockedOnlineModal}
            onClose={() => setShowLockedOnlineModal(false)}
            document={isLockedOnline ? (document ?? null) : null}
            resolveLabel={prefillResolvers.resolveLabel}
            onCreateNewDocument={handleCreateFromConflict}
          />


          {/* Document Tabs + Help/Notes toggle — full width, outside px-6 padding */}
          <div className="flex items-center justify-between border-t">
            <DocumentTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showSummary={showSummaryTab}
              showSignature={!!template?.signature_config}
              signatureEnabled={signatureTabSlots.length > 0}
            />
            <div className="pr-6">
              {hasPanel && <PanelToggleButton hasPanel={hasPanel} />}
            </div>
          </div>

          {/* Error Navigation — below the tabs, form tab only. Hidden
               instead of unmounted to preserve the tracked error position */}
          {!issueControls && (
            <div className={activeTab !== 'document' ? 'hidden' : ''}>
              <ErrorNavigation errors={finishErrors} />
              {externalErrors.length > 0 && (
                <div className="px-6">
                  <ExternalErrorsBanner
                    errors={externalErrors}
                    resolveHref={resolveOnlineErrorHref}
                    heading="Some issues must be fixed in the online app:"
                    footer="Open Mon Ami in the online app when you have an internet connection to fix these."
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area — overflow-clip prevents browser focus-scroll from
             shifting this container when sr-only checkbox inputs receive focus.
             FormDataStoreProvider owns formData in uncontrolled mode so this
             component does not re-render on every field change. */}
        <FormDataStoreProvider
          initialFormData={initialFormDataRef.current}
          actionsRef={formDataActionsRef}
          onChange={handleFormDataChange}
        >
        {/* Datetime answers are UTC on the wire; fields convert to the person's
             declared zone for display and convert edits back to UTC. */}
        <TimezoneProvider timezone={currentPerson?.timezone}>
        <div className="super-form flex-1 flex overflow-clip relative min-h-0">
          {/* Navigation Sidebar — hidden instead of unmounted to preserve state.
              formData is consumed via the FormDataStoreProvider above. */}
          {template?.super_form?.definition && (
            <div className={activeTab !== 'document' ? 'hidden bg-white' : 'bg-white'}>
              <DocumentNavigationSidebar
                formDefinition={template.super_form.definition as SuperFormDefinition}
                defaultCollapsed={sidebarCollapsed}
                defaultWidth={sidebarWidth}
                showSubsections={!!template.super_form.definition?.interrai}
                onCollapsedChange={handleSidebarCollapsedChange}
                onWidthChange={handleSidebarWidthChange}
              />
            </div>
          )}

          {/* Content — both tabs stay mounted to avoid expensive remount of 583+ fields */}
          <div className={`flex-1 overflow-auto bg-white ${activeTab !== 'document' ? 'hidden' : ''}`} ref={(el) => { scrollRefs.current['document'] = el; }}>
            <div className="p-6">
              {isPhone && <DocumentMeta {...metaProps} className="document-editor__meta--content" />}
              {hasRequiredData && formDataInitialized && template?.super_form?.definition && (
                <ContactModelDataProvider definition={template.super_form.definition as SuperFormDefinition}>
                  <PrefillProviderFromStore
                    prefills={document?.field_prefills}
                    prefillResolvers={prefillResolvers}
                    confirmedPrefillsSet={confirmedPrefillsSet}
                    confirmAllEnabled={template?.confirm_all_enabled !== false}
                    clientName={decoratedClient?.person.displayName}
                    onPrefillConfirmed={handlePrefillConfirmed}
                    onPrefillsConfirmed={handlePrefillsConfirmed}
                  >
                    {!issueControls && (
                      <>
                        <ParcErrorNavSync
                          prefills={document?.field_prefills}
                          prefillResolvers={prefillResolvers}
                          confirmedPrefillsSet={confirmedPrefillsSet}
                          formDef={template?.super_form?.definition?.form as Record<string, unknown> | undefined}
                          setPfSectionErrors={setPfSectionErrors}
                        />
                        <PrefillBanner />
                      </>
                    )}
                    {/* Floating issue controls (Blockers / Warnings / Prefilled) and
                        the live soft-validation warnings. Around the SuperForm: its
                        questions read their warning state from here, and answers
                        saved from the warning modal go through it. Without the
                        controls it has no warnings to show. */}
                    <IssuesProvider
                      definition={template.super_form.definition as SuperFormDefinition}
                      client={decoratedClient!}
                      readonly={isFormReadonly || !templateOfflineEnabled}
                      blockers={blockers}
                      serverWarnings={serverWarnings}
                      warningDismissals={warningDismissals}
                      onWarningDismissed={handleWarningDismissed}
                      onWarningUndismissed={handleWarningUndismissed}
                      onBlur={saveFormData}
                      resolveExternalErrorHref={resolveOnlineErrorHref}
                      actionsRef={issuesActionsRef}
                    >
                      <FieldPermissionsProvider fieldPermissions={document?.field_permissions}>
                        {template?.field_notes_enabled !== false ? (
                          <NotesProviderFromStore
                            definition={template.super_form.definition as SuperFormDefinition}
                            onOpenNotesToField={handleOpenNotesToField}
                          >
                            <DateCompletedInput
                              value={dateCompletedValue(document)}
                              onChange={handleRecordedAtChange}
                              readonly={isFormReadonly || !templateOfflineEnabled}
                            />
                            <SuperForm
                              key={template.id}
                              definition={template.super_form.definition as SuperFormDefinition}
                              onChange={handleFormDataChange}
                              onBlur={saveFormData}
                              errors={validationErrors}
                              readonly={isFormReadonly || !templateOfflineEnabled}
                              client={decoratedClient!}
                              {...revealedFieldsProps}
                            />
                          </NotesProviderFromStore>
                        ) : (
                          <>
                            <DateCompletedInput
                              value={dateCompletedValue(document)}
                              onChange={handleRecordedAtChange}
                              readonly={isFormReadonly || !templateOfflineEnabled}
                            />
                            <SuperForm
                              key={template.id}
                              definition={template.super_form.definition as SuperFormDefinition}
                              onChange={handleFormDataChange}
                              onBlur={saveFormData}
                              errors={validationErrors}
                              readonly={isFormReadonly || !templateOfflineEnabled}
                              client={decoratedClient!}
                              {...revealedFieldsProps}
                            />
                          </>
                        )}
                      </FieldPermissionsProvider>
                      {issueControls && <IssueControls active={activeTab === 'document'} />}
                    </IssuesProvider>
                  </PrefillProviderFromStore>

                </ContactModelDataProvider>
              )}
            </div>
          </div>

          {(summaryVisited || activeTab === 'summary') && (
            <div className={`flex-1 overflow-auto bg-white ${activeTab !== 'summary' ? 'hidden' : ''}`} ref={(el) => { scrollRefs.current['summary'] = el; }}>
              <div className="p-6">
                {isPhone && <DocumentMeta {...metaProps} className="document-editor__meta--content pl-6" />}
                <SummaryTab
                  // GO's Document widens sync_error_type (offline-only terminal states);
                  // SummaryTab ignores that field, so cast to the package type at the boundary.
                  document={(document as unknown as SuperFormDocument) ?? undefined}
                  template={template ?? undefined}
                  client={decoratedClient ?? undefined}
                  onFieldClick={handleSummaryFieldClick}
                />
              </div>
            </div>
          )}

          {/* Signature tab — collects signatures during the signing window, read-only display once completed */}
          {activeTab === 'signature' && (
            <div className="flex-1 overflow-auto bg-white" ref={(el) => { scrollRefs.current['signature'] = el; }}>
              <div className="p-6">
                {isPhone && <DocumentMeta {...metaProps} className="document-editor__meta--content pl-6" />}
                <SignatureTab
                  signatures={signatureTabSlots}
                  currentSignerName={currentPersonName}
                  onSign={isWaitingForSignatures && !isComplete ? handleTabSign : undefined}
                />
              </div>
            </div>
          )}

          {/* Tabbed Popover - shares space with form.
              notes are derived from the FormDataStoreProvider above. */}
          <DocumentPanelWrapper
            formDefinition={template?.super_form?.definition as SuperFormDefinition | undefined}
            canEdit={!isFormReadonly && templateOfflineEnabled}
            onNoteChange={handleNoteChange}
            onNoteSave={handleNoteSave}
            onNoteDelete={handleNoteDelete}
            onFieldClick={handleSummaryFieldClick}
            focusFieldSlug={focusedNoteField}
            focusFieldLabel={focusedNoteFieldLabel}
            onFocusFieldCleared={handleFocusFieldCleared}
            fieldNotesEnabled={template?.field_notes_enabled !== false}
            documentNoteEnabled={template?.document_note_enabled !== false}
            notesVisible={activeTab !== 'summary'}
            defaultWidth={panelWidth}
            onWidthChange={handlePanelWidthChange}
          />
        </div>
        </TimezoneProvider>
        </FormDataStoreProvider>

      </div>
      </HelpDocumentProvider>
    </ErrorReporterProvider>
  );
}
